import type { Tone } from "@/components/ui/badge";
import { AUDIT_ACTIONS, type AuditAction } from "@/contracts";

import i18n from "@/i18n";

/**
 * The trail outlives this file: an action recorded by an older version of
 * the code has no entry here and must still read as something. Falling back
 * to the raw identifier is honest — better a reader who sees
 * `device.retired` than one who sees a blank cell.
 */
export function actionLabel(action: string): string {
  if (isKnownAction(action)) {
    return i18n.t(`pages:auditActions.${action}`);
  }

  return action;
}

export function isKnownAction(action: string): action is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(action);
}

/**
 * The group an action belongs to, taken from the prefix before the dot.
 * Used for the optgroups in the filter.
 */
export function groupLabel(group: string): string {
  const key = `pages:auditGroups.${group}`;

  return i18n.t(key, { defaultValue: group });
}

export function groupOf(action: string): string {
  return action.split(".")[0] ?? "";
}

/**
 * Only the destructive and security-relevant actions get a tone. Colouring
 * every one of them would turn the column into decoration and leave nothing
 * standing out, which is the one job it has.
 */
const TONED_ACTIONS: Partial<Record<AuditAction, Tone>> = {
  "organization.suspend": "danger",
  "device.deactivate": "warn",
  "user.invite": "info",
  "user.membership_granted": "info",
  "organization.create": "info",
};

export function actionTone(action: string): Tone {
  return TONED_ACTIONS[action as AuditAction] ?? "neutral";
}

/**
 * Metadata keys as prose. Anything not listed falls back to the key itself,
 * so a new key added on the API shows up rather than disappearing.
 */
export function metadataLabel(key: string): string {
  return i18n.t(`pages:auditMeta.${key}`, { defaultValue: key });
}

export function metadataValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }

  if (Array.isArray(value)) {
    return value.map((item) => metadataValue(item)).join(", ");
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }

  // An object or something exotic. The JSON is worth more to whoever is
  // reading the trail than "[object Object]", and `stringify` returns
  // undefined rather than throwing on a value it cannot represent.
  return JSON.stringify(value) ?? `(${typeof value})`;
}

const ACTION_GROUP_KEYS = [
  "auth",
  "organization",
  "user",
  "site",
  "asset",
  "device",
  "incident",
  "alert_rule",
  "messaging_contact",
  "notification",
  "platform_settings",
] as const;

/** Optgroup labels for the audit action filter, resolved in the active locale. */
export function actionGroupEntries(): { group: string; label: string }[] {
  return ACTION_GROUP_KEYS.map((group) => ({ group, label: groupLabel(group) }));
}
